import Modal from '../Shared/Modal';
import { getThemedBg } from '../../utility/themeVars';

/**
 * Themed lightbox that shows a service image at full size, uncropped.
 *
 * Service thumbnails on the public booking page render with object-cover inside
 * a fixed-ratio box (16:9 banner on mobile, square on desktop), so off-ratio
 * images get cropped. This overlay lets a visitor see the whole image.
 *
 * Purely presentational — the parent owns the open/onClose state. Reuses
 * Shared/Modal for the backdrop, focus trap, escape/backdrop-to-close, and the
 * provider's brand theming (matching ProviderBioModal). Headless UI's Dialog
 * portals to document.body, escaping PublicBookingLayout's inline theme-var
 * scope, so Modal re-injects the --theme-* vars onto the DialogPanel via
 * themeStyle below — keeping var(--theme-*) resolvable inside the panel.
 *
 * @param {boolean} open - Whether the lightbox is visible.
 * @param {Function} onClose - Callback invoked when the lightbox should close.
 * @param {string} imageUrl - Full-resolution image URL to display.
 * @param {string} alt - Accessible label / modal title (the service name).
 * @param {string} primaryColor - Booking page primary color (Tailwind name).
 * @param {string} themeMode - 'light' or 'dark'.
 */
const ImageLightbox = ({ open, onClose, imageUrl, alt, primaryColor, themeMode }) => {
  // Nothing to show — don't mount the modal at all.
  if (!imageUrl) {
    return null;
  }

  const themedBg = getThemedBg(themeMode, primaryColor);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={alt || 'Image'}
      size="xl"
      themedBg={themedBg}
      themeStyle={themedBg.style}
    >
      {/* object-contain (not cover) is the whole point: the full image is
          shown letterboxed, never cropped. */}
      <img
        src={imageUrl}
        alt={alt || ''}
        className="mx-auto h-auto max-h-[80vh] w-full rounded-card object-contain"
      />
    </Modal>
  );
};

export default ImageLightbox;
